/**
 * triggerLoginPrewarm — fire-and-forget call to the per-user
 * pre-warm endpoint right after login (perf #15/15, 2026-04-25).
 *
 * The backend identifies the user's most-likely-next learning-path
 * node and fills its question bank in a background task. By the
 * time the student opens an exercise, the questions are queued and
 * rendered without waiting on AI generation.
 *
 * Usage (from App.js post-login flow):
 *
 *   triggerLoginPrewarm(accessToken);   // resolves to the result
 *
 * The promise NEVER rejects — failures are swallowed because this is
 * a perf optimization, not a feature gate. We log them to console for
 * debugging but the user shouldn't see anything.
 *
 * Marked with `x-background-request: true` so the global 401
 * interceptor in App.js skips auto-logout if the token has gone
 * stale during a multi-tab session — same pattern as the analytics
 * and learning-paths prefetches.
 */

import axios from 'axios';
import { API_URL } from '../config';

export async function triggerLoginPrewarm(accessToken) {
  if (!accessToken) {
    return { skipped: true, reason: 'no-token' };
  }
  try {
    const resp = await axios.post(
      `${API_URL}/api/v1/learning-paths/prewarm-mine`,
      null,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-background-request': 'true',
        },
        // Aggressive timeout — the endpoint should return ~immediately
        // because the AI work happens in a server-side background task.
        // If the server takes >5s to even pick a node, give up; the
        // global cron will eventually warm popular nodes anyway.
        timeout: 5000,
      },
    );
    return { skipped: false, ...resp.data };
  } catch (err) {
    // Quiet failure mode — never surface to the user.
    // eslint-disable-next-line no-console
    console.debug('login prewarm skipped:', err?.message || err);
    return { skipped: true, reason: 'network-or-server-error' };
  }
}

export default triggerLoginPrewarm;
